import React, { useEffect, useRef, useState } from "react";

const SDK_SCRIPT_ID = "coze-chat-app-sdk-script";
const SDK_URL =
  "/static/js/coze-chat-app-sdk.1.2.0-beta.20.cn.js";

type CozeWebChatClient = {
  destroy?: () => void;
  showChatBot?: () => void;
};

type CozeWebSDKGlobal = {
  WebChatClient: new (options: Record<string, unknown>) => CozeWebChatClient;
};

declare global {
  interface Window {
    CozeWebSDK?: CozeWebSDKGlobal;
  }
}

const loadCozeWebSDK = (): Promise<CozeWebSDKGlobal> =>
  new Promise((resolve, reject) => {
    if (window.CozeWebSDK?.WebChatClient) {
      resolve(window.CozeWebSDK);
      return;
    }

    const existingScript = document.getElementById(
      SDK_SCRIPT_ID,
    ) as HTMLScriptElement | null;

    if (existingScript) {
      existingScript.addEventListener("load", () => {
        if (window.CozeWebSDK?.WebChatClient) {
          resolve(window.CozeWebSDK);
          return;
        }
        reject(new Error("Coze SDK loaded but WebChatClient is missing."));
      });
      existingScript.addEventListener("error", () => {
        reject(new Error("Failed to load Coze SDK script."));
      });
      return;
    }

    const script = document.createElement("script");
    script.id = SDK_SCRIPT_ID;
    script.src = SDK_URL;
    script.async = true;
    script.onload = () => {
      if (window.CozeWebSDK?.WebChatClient) {
        resolve(window.CozeWebSDK);
        return;
      }
      reject(new Error("Coze SDK loaded but WebChatClient is missing."));
    };
    script.onerror = () => {
      reject(new Error("Failed to load Coze SDK script."));
    };
    document.body.appendChild(script);
  });

const TChat: React.FC = () => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const clientRef = useRef<CozeWebChatClient | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [rootHeight] = useState<number>(() =>
    typeof window !== "undefined" ? Math.max(window.innerHeight - 24, 320) : 800
  );

  useEffect(() => {
    const run = async () => {
      if (!rootRef.current) {
        setError(new Error("chatBot.el container is not ready."));
        return;
      }

      setError(null);
      try {
        if (clientRef.current?.destroy) {
          clientRef.current.destroy();
          clientRef.current = null;
        }

        const sdk = await loadCozeWebSDK();
        const root = rootRef.current;

        clientRef.current = new sdk.WebChatClient({
          config: {
            type: "bot",
            bot_id: "7626334428126117924",
            isIframe: false,
          },
          auth: {
            type: "token",
            token:
              "sat_z84vFrtGz3Iff6CvQ2xPdOQnQPqf6ms0svQFvzgGYBxiFMpwxwQzCou0WImSB5oA",
            onRefreshToken: async () => "token",
          },
          userInfo: {
            id: "user",
            url: "https://lf-coze-web-cdn.coze.cn/obj/eden-cn/lm-lgvj/ljhwZthlaukjlkulzlp/coze/coze-logo.png",
            nickname: "User",
          },
          ui: {
            base: {
              icon: "https://lf-coze-web-cdn.coze.cn/obj/eden-cn/lm-lgvj/ljhwZthlaukjlkulzlp/coze/chatsdk-logo.png",
              layout: "pc",
              lang: "en",
              zIndex: 1000,
            },
            header: {
              isShow: false,
              isNeedClose: false,
            },
            asstBtn: {
              isNeed: false,
            },
            footer: {
              isShow: true,
              expressionText: "Powered by ...",
            },
            chatBot: {
              title: "Coze Bot",
              uploadable: true,
              width: 390,
              el: root,
            },
          },
        });
        const openChat = () => {
          clientRef.current?.showChatBot?.();
          console.log("[TChat] showChatBot called");
        };
        requestAnimationFrame(() => {
          openChat();
          window.setTimeout(openChat, 120);
        });
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to initialize Coze."),
        );
        console.error(err);
      }
    };

    const styleEl = document.createElement("style");
    styleEl.id = "tchat-root-margin-reset";
    styleEl.textContent = `
      #root,
      #root * {
        margin: 0 !important;
      }
    `;
    document.head.appendChild(styleEl);

    void run();

    return () => {
      if (clientRef.current?.destroy) {
        clientRef.current.destroy();
        clientRef.current = null;
      }
      styleEl.remove();
    };
  }, []);

  if (error) {
    return (
      <div
        id="root"
        ref={rootRef}
        style={{ margin: 0, width: "100%", height: `${rootHeight}px` }}
      />
    );
  }

  return (
    <div
      id="root"
      ref={rootRef}
      style={{ margin: 0, width: "100%", height: `${rootHeight}px` }}
    />
  );
};

export default TChat;
